import moment from 'moment'
import { efacturaStreetName, efacturaCountrySubentity } from '@/helpers/address.js'
import { docHtRon, docTtcRon, docVatRateOf } from '@/mixins/documentTotals.js'

/**
 * Générateur du XML UBL e-Factura — SOURCE UNIQUE.
 *
 * ---------------------------------------------------------------------------
 * POURQUOI CE MODULE EXISTE
 * ---------------------------------------------------------------------------
 * Le XML envoyé à l'ANAF était généré dans les fiches facture, en trois copies
 * quasi identiques (`invoicing/OperationView.vue`, `invoicing/FirewoodInvoiceView.vue`,
 * `broker/invoicing/OperationView.vue`), chacune dépendant de l'état du
 * composant Vue qui l'hébergeait. L'envoi groupé depuis la liste
 * `/efacturalist-out` ne pouvait donc pas réutiliser ce code : il aurait fallu
 * en écrire une quatrième copie, et deux copies d'un document LÉGAL finissent
 * toujours par diverger.
 *
 * Ici, le générateur est une fonction pure de ses données. La fiche et la liste
 * appellent la MÊME fonction : pour un document donné, elles produisent le même
 * XML, au caractère près.
 *
 * ---------------------------------------------------------------------------
 * CE QU'IL NE FAUT PAS « MODERNISER »
 * ---------------------------------------------------------------------------
 *   - `doc.createElement('cbc:ID')` (et non `createElementNS`) : les préfixes
 *     sont déclarés à la main sur l'élément racine et re-sérialisés tels quels.
 *     Passer aux namespaces changerait la sortie acceptée aujourd'hui par l'ANAF.
 *   - l'enchaînement `serializeToString` -> préfixe `<?xml …?>` -> `formatXML`
 *     (indentation 2 espaces, sauts `\r\n`) : c'est cette chaîne exacte que
 *     l'ANAF transforme en PDF de contrôle.
 *   - la correction d'arrondi de la fenêtre `0.009 <= |diff| <= 0.01` : elle
 *     n'est pas morte, elle absorbe le résidu sur la première ligne positive.
 *
 * ---------------------------------------------------------------------------
 * STRUCTURE
 * ---------------------------------------------------------------------------
 * Deux formes de document (facture, storno) × deux résolutions d'identité
 * partenaire (standard, bois de feu). Le corps XML des quatre combinaisons est
 * rigoureusement le même code : seules diffèrent les valeurs `partner*`.
 *
 * @typedef {Object} EfacturaContext
 * @property {Object} operation      payload de GET /operation/data/:id  (user_data)
 * @property {Object} billingAddress normalizeAddress(...)               (addressbilling)
 * @property {Array}  lines          payload.lines de /getdocumentlines  (productlist)
 * @property {Object} totals         payload.totals de /getdocumentlines (documentTotals)
 * @property {number|string} idoperation  utilisé uniquement dans les messages d'erreur
 */

// ---------------------------------------------------------------------------
// Constantes de l'émetteur
// ---------------------------------------------------------------------------
const SUPPLIER = {
  cif: '33531190',
  address: 'Strada Principala 115A, Teiu 337291',
  city: 'Lapugiu de Jos',
  judet: 'RO-HD',
  country: 'RO',
  name: 'XYLO IMPACT S.R.L.',
  vat: 'RO33531190',
  j: 'J2022000979207'
}
const BANK_IBAN = 'RO22BTRLRONCRT0267032501'
const BANK_NAME = 'Banca Transilvania'

// ---------------------------------------------------------------------------
// Erreurs typées — l'appelant décide comment les afficher
// ---------------------------------------------------------------------------

/** Le total du document ne correspond pas à la somme de ses lignes : génération refusée. */
function totalsMismatchError (message) {
  const err = new Error(message)
  err.isEfacturaTotalsMismatch = true
  return err
}

/** Judeţ introuvable sur l'adresse de facturation (facture bois de feu). */
function unknownJudetError (placeName) {
  const err = new Error(
    `Le judeţ de l'adresse de facturation n'a pas pu être déterminé${placeName ? ` (« ${placeName} »)` : ''}. ` +
    'Corrigez la localité de l\'adresse dans ÉDITER : l\'ANAF exige le code du judeţ.'
  )
  err.isEfacturaUnknownJudet = true
  return err
}

// ---------------------------------------------------------------------------
// Helpers DOM / formatage
// ---------------------------------------------------------------------------

function addElement (doc, parent, name, content) {
  const element = doc.createElement(name)
  element.textContent = content
  parent.appendChild(element)
  return element
}

function addComplexElement (doc, parent, name) {
  const element = doc.createElement(name)
  parent.appendChild(element)
  return element
}

function formatXML (inputXml) {
  let formatted = ''
  const reg = /(>)(<)(\/*)/g
  const xml = inputXml.replace(reg, '$1\r\n$2$3')
  let pad = 0
  xml.split('\r\n').forEach(function (node) {
    let indent = 0
    if (node.match(/.+<\/\w[^>]*>$/)) {
      indent = 0
    } else if (node.match(/^<\/\w/)) {
      if (pad !== 0) {
        pad -= 1
      }
    } else if (node.match(/^<\w[^>]*[^/]>.*$/)) {
      indent = 1
    } else {
      indent = 0
    }

    let padding = ''
    for (let i = 0; i < pad; i++) {
      padding += '  '
    }

    formatted += padding + node + '\r\n'
    pad += indent
  })

  return formatted
}

/** Bucureşti : l'ANAF attend le secteur, pas le nom de la ville. */
function getPlaceBucuresti (placeName) {
  if (placeName.includes('Bucureşti 1')) return 'SECTOR1'
  else if (placeName.includes('Bucureşti 2')) return 'SECTOR2'
  else if (placeName.includes('Bucureşti 3')) return 'SECTOR3'
  else if (placeName.includes('Bucureşti 4')) return 'SECTOR4'
  else if (placeName.includes('Bucureşti 5')) return 'SECTOR5'
  else if (placeName.includes('Bucureşti 6')) return 'SECTOR6'
  else if (placeName.includes('Bucureşti 7')) return 'SECTOR5'
  else if (placeName.includes('Bucureşti 8')) return 'SECTOR3'
  else if (placeName.includes('Bucureşti 9')) return 'SECTOR3'
  else return placeName
}

function getJudetAbbreviation (judetName) {
  const judete = {
    'Bucureşti': 'B',
    'Alba': 'AB',
    'Arad': 'AR',
    'Argeş': 'AG',
    'Bacău': 'BC',
    'Bihor': 'BH',
    'Bistriţa-Năsăud': 'BN',
    'Botoşani': 'BT',
    'Brăila': 'BR',
    'Braşov': 'BV',
    'Buzău': 'BZ',
    'Călăraşi': 'CL',
    'Caraş-Severin': 'CS',
    'Cluj': 'CJ',
    'Constanţa': 'CT',
    'Covasna': 'CV',
    'Dâmboviţa': 'DB',
    'Dolj': 'DJ',
    'Galaţi': 'GL',
    'Giurgiu': 'GR',
    'Gorj': 'GJ',
    'Harghita': 'HR',
    'Hunedoara': 'HD',
    'Ialomiţa': 'IL',
    'Iaşi': 'IS',
    'Ilfov': 'IF',
    'Maramureş': 'MM',
    'Mehedinţi': 'MH',
    'Mureş': 'MS',
    'Neamţ': 'NT',
    'Olt': 'OT',
    'Prahova': 'PH',
    'Sălaj': 'SJ',
    'Satu Mare': 'SM',
    'Sibiu': 'SB',
    'Suceava': 'SV',
    'Teleorman': 'TR',
    'Timiş': 'TM',
    'Tulcea': 'TL',
    'Vâlcea': 'VL',
    'Vaslui': 'VS',
    'Vrancea': 'VN'
  }

  return judete[judetName]
}

/**
 * Repli quand `region` est vide (vieilles adresses issues de xml_macdo) :
 * déduire le judeţ de la localité (ex. TIMISOARA -> RO-TM).
 * Renvoie "" si inconnue — surtout pas un judeţ par défaut.
 */
function getJudetFromPlace (placeName) {
  if (!placeName || typeof placeName !== 'string') return ''
  const placeToJudet = {
    'TIMISOARA': 'RO-TM',
    'TIMIŞOARA': 'RO-TM',
    'ARAD': 'RO-AR',
    'BUCURESTI': 'RO-B',
    'BUCUREŞTI': 'RO-B',
    'CONSTANTA': 'RO-CT',
    'CONSTANŢA': 'RO-CT',
    'CLUJ': 'RO-CJ',
    'CLUJ-NAPOCA': 'RO-CJ',
    'BRASOV': 'RO-BV',
    'BRAŞOV': 'RO-BV',
    'SIBIU': 'RO-SB',
    'CRAIOVA': 'RO-DJ',
    'GALATI': 'RO-GL',
    'GALAŢI': 'RO-GL',
    'IASI': 'RO-IS',
    'IAŞI': 'RO-IS',
    'ORADEA': 'RO-BH',
    'BACAU': 'RO-BC',
    'BACĂU': 'RO-BC',
    'TARGU MURES': 'RO-MS',
    'TÂRGU MUREŞ': 'RO-MS',
    'PLOIESTI': 'RO-PH',
    'PLOIEŞTI': 'RO-PH',
    'BRAILA': 'RO-BR',
    'BRĂILA': 'RO-BR',
    'SATU MARE': 'RO-SM'
  }
  const key = String(placeName).trim().toUpperCase()
  return placeToJudet[key] || ''
}

// ---------------------------------------------------------------------------
// Blocs d'identité (cac:Party)
// ---------------------------------------------------------------------------

/** Émetteur, facture comme storno : PartyIdentification/cbc:ID = numéro de TVA (RO33531190). */
function addPartyProviderDetails (doc, parent, idValue, streetName, cityName, countrySubentity, identificationCode, registrationName, taxSchemeID, idVATValue, supplierJ) {
  const partyIdentification = addComplexElement(doc, parent, 'cac:PartyIdentification')
  addElement(doc, partyIdentification, 'cbc:ID', idVATValue)

  const postalAddress = addComplexElement(doc, parent, 'cac:PostalAddress')
  addElement(doc, postalAddress, 'cbc:StreetName', streetName)
  addElement(doc, postalAddress, 'cbc:CityName', cityName)
  addElement(doc, postalAddress, 'cbc:CountrySubentity', countrySubentity)
  const country = addComplexElement(doc, postalAddress, 'cac:Country')
  addElement(doc, country, 'cbc:IdentificationCode', identificationCode)
  const partyTaxScheme = addComplexElement(doc, parent, 'cac:PartyTaxScheme')
  addElement(doc, partyTaxScheme, 'cbc:CompanyID', taxSchemeID)
  const taxScheme = addComplexElement(doc, partyTaxScheme, 'cac:TaxScheme')
  addElement(doc, taxScheme, 'cbc:ID', 'VAT')

  const partyLegalEntity = addComplexElement(doc, parent, 'cac:PartyLegalEntity')
  addElement(doc, partyLegalEntity, 'cbc:RegistrationName', registrationName)
  addElement(doc, partyLegalEntity, 'cbc:CompanyID', supplierJ)
}

/** Client personne morale. */
function addPartyClientDetails (doc, parent, idValue, streetName, cityName, countrySubentity, identificationCode, registrationName, taxSchemeID, clientJ) {
  const partyIdentification = addComplexElement(doc, parent, 'cac:PartyIdentification')
  addElement(doc, partyIdentification, 'cbc:ID', idValue)

  const postalAddress = addComplexElement(doc, parent, 'cac:PostalAddress')
  addElement(doc, postalAddress, 'cbc:StreetName', streetName)
  addElement(doc, postalAddress, 'cbc:CityName', cityName)
  addElement(doc, postalAddress, 'cbc:CountrySubentity', countrySubentity)
  const country = addComplexElement(doc, postalAddress, 'cac:Country')
  addElement(doc, country, 'cbc:IdentificationCode', identificationCode)
  const partyTaxScheme = addComplexElement(doc, parent, 'cac:PartyTaxScheme')
  addElement(doc, partyTaxScheme, 'cbc:CompanyID', taxSchemeID)
  const taxScheme = addComplexElement(doc, partyTaxScheme, 'cac:TaxScheme')
  addElement(doc, taxScheme, 'cbc:ID', 'VAT')

  const partyLegalEntity = addComplexElement(doc, parent, 'cac:PartyLegalEntity')
  addElement(doc, partyLegalEntity, 'cbc:RegistrationName', registrationName)
  addElement(doc, partyLegalEntity, 'cbc:CompanyID', clientJ)
}

/** Client personne physique : ni PartyIdentification, ni PartyTaxScheme. */
function addPartyClientFISICDetails (doc, parent, streetName, cityName, countrySubentity, identificationCode, registrationName, clientJ) {
  const postalAddress = addComplexElement(doc, parent, 'cac:PostalAddress')
  addElement(doc, postalAddress, 'cbc:StreetName', streetName)
  addElement(doc, postalAddress, 'cbc:CityName', cityName)
  addElement(doc, postalAddress, 'cbc:CountrySubentity', countrySubentity)
  const country = addComplexElement(doc, postalAddress, 'cac:Country')
  addElement(doc, country, 'cbc:IdentificationCode', identificationCode)

  const partyLegalEntity = addComplexElement(doc, parent, 'cac:PartyLegalEntity')
  addElement(doc, partyLegalEntity, 'cbc:RegistrationName', registrationName)
  addElement(doc, partyLegalEntity, 'cbc:CompanyID', clientJ)
}

// ---------------------------------------------------------------------------
// Identité du client : CUI sur la fiche = société, rien = personne physique
// ---------------------------------------------------------------------------
//
// Le 06/10/2026 à 06:27, RO2026_01525 (Recep Plus, CUI 3190694) est partie à
// l'ANAF avec 0000000000000 : la fiche n'avait pas encore de CUI (ajouté à
// 06:44). L'ANAF l'a acceptée sans pouvoir la remettre au client (BT-47 lipsa
// identificator). Règle (Chady, 2026-10-29), identique côté API
// (api/app/helpers/efacturaBuyer.js) :
//   - la FICHE seule dit qui est le client : un CUI encodé (numéro de TVA, CUI
//     de 2 à 10 chiffres ou J) = une société, qui part avec cet identifiant ;
//     rien (ou un CNP) = une personne physique, 13 zéros. Le commutateur
//     COMPANY/PERSON n'est PAS utilisé (la borne met tout le monde en COMPANY),
//     le nom non plus — « quand ils encodent le CUI c'est une société, quand
//     ils ne l'encodent pas c'est une personne physique » ;
//   - le filet de sécurité est APRÈS l'envoi : le robot « messages ANAF » relit
//     le BT-47 et marque en rouge une facture partie en zéros dont la fiche
//     porte un CUI aujourd'hui ;
//   - un storno exige un client avec CUI sur sa fiche.

const NATURAL_PERSON_ID = '0000000000000'
const digitsOf = (v) => String(v === null || v === undefined ? '' : v).replace(/\D/g, '')

/**
 * Société ou personne physique, d'après la fiche seule (miroir de
 * api/app/helpers/efacturaBuyer.buyerIdentifierOnRecord).
 * @returns {{kind:'COMPANY'|'PERSON', id:?string, why:string}}
 */
export function buyerIdentifierOnRecord (person) {
  const p = person || {}
  const vat = digitsOf(p.vat_number)
  const cui = digitsOf(p.cui)
  const j = String(p.inmatriculare_j === null || p.inmatriculare_j === undefined ? '' : p.inmatriculare_j).trim()
  if (vat) return { kind: 'COMPANY', id: String(p.vat_number).trim(), why: 'VAT number on record' }
  if (cui && cui.length >= 2 && cui.length <= 10) return { kind: 'COMPANY', id: cui, why: 'CUI on record' }
  if (j) return { kind: 'COMPANY', id: j, why: 'J number on record' }
  if (cui.length === 13) return { kind: 'PERSON', id: cui, why: 'CNP on record' }
  return { kind: 'PERSON', id: null, why: 'no CUI on record' }
}

/** @deprecated Le commutateur COMPANY/PERSON n'est plus utilisé pour l'e-factura : voir buyerIdentifierOnRecord. */
export function isCompanyPerson (person) {
  const v = person && person.iscompany
  return !(v === false || v === 'false' || v === 0 || v === '0')
}

/**
 * Ce qui empêche d'émettre une e-factura pour ce client, en anglais (langue de
 * l'UI) ; '' si rien. Sert aux boutons des fiches ET au générateur, pour que
 * l'écran dise la même chose que le refus.
 */
export function efacturaBuyerIdentityProblem (person, { storno = false } = {}) {
  if (!person) return 'The document has no customer.'
  const name = person.company_name || '?'
  if (buyerIdentifierOnRecord(person).kind === 'COMPANY') return ''
  if (storno) return `"${name}" has no CUI on the customer record (private person) — an e-Factura credit note needs a customer with a CUI (or VAT number): enter it on the record, then try again.`
  return ''
}

function buyerIdentityError (ctx, problem) {
  const err = new Error(`e-Factura ${ctx.operation.invoice_no || ctx.idoperation}: ${problem}`)
  err.isEfacturaMissingCui = true
  return err
}

/**
 * Identifiants du client tels qu'ils iront dans l'XML.
 *   company : false → personne physique : 13 zéros, ni PartyIdentification ni PartyTaxScheme
 *   cif     : CUI en chiffres — jamais les zéros pour une société
 *   vat     : numéro de TVA « RO… » si la fiche en a un, sinon '' (société non assujettie)
 *   legalId : PartyLegalEntity/CompanyID (BT-47) — J d'immatriculation, sinon le CUI
 * `firewood` : identifiants de la borne, irréguliers (CUI parfois sans préfixe RO).
 */
function resolveBuyerIdentity (ctx, { storno = false, firewood = false } = {}) {
  const person = ctx.operation.people[0] || {}
  const problem = efacturaBuyerIdentityProblem(person, { storno })
  if (problem) throw buyerIdentityError(ctx, problem)
  if (buyerIdentifierOnRecord(person).kind !== 'COMPANY') {
    return { company: false, cif: NATURAL_PERSON_ID, vat: '', legalId: NATURAL_PERSON_ID }
  }
  const rawVat = String(person.vat_number || '').trim()
  const rawJ = String(person.inmatriculare_j || '').trim()
  // Société identifiée par son seul J : c'est lui qui va en BT-47.
  const cif = digitsOf(rawVat) || digitsOf(person.cui) || rawJ
  let vat = ''
  if (rawVat) vat = firewood && !/^[A-Z]{2}/i.test(rawVat) ? `RO${rawVat}` : rawVat
  let legalId = rawJ
  if (firewood && rawJ && !/^[A-Z]{2}/i.test(rawJ)) legalId = `RO${rawJ}`
  if (!legalId) legalId = (firewood && vat) || cif
  return { company: true, cif, vat, legalId }
}

/**
 * Bloc client selon son identité :
 *   - personne physique : ni PartyIdentification ni PartyTaxScheme, CompanyID = 13 zéros ;
 *   - société non assujettie : idem, mais CompanyID (BT-47) = son CUI — c'est là
 *     que l'ANAF lit l'identifiant quand il n'y a pas de numéro de TVA ;
 *   - société assujettie : PartyIdentification + PartyTaxScheme (BT-48 = RO…).
 */
function addPartyClient (doc, parent, partner) {
  if (!partner.company) {
    addPartyClientFISICDetails(doc, parent, partner.address, partner.city, partner.judet, partner.country, partner.name, NATURAL_PERSON_ID)
  } else if (!partner.vat) {
    addPartyClientFISICDetails(doc, parent, partner.address, partner.city, partner.judet, partner.country, partner.name, partner.cif)
  } else {
    addPartyClientDetails(doc, parent, partner.cif, partner.address, partner.city, partner.judet, partner.country, partner.name, partner.vat, partner.legalId)
  }
}

/** Factures « ERP » : identité de la fiche + adresse de facturation. */
function standardPartner (ctx, opts) {
  const identity = resolveBuyerIdentity(ctx, opts)
  const person = ctx.operation.people[0]
  return Object.assign(identity, {
    address: efacturaStreetName(ctx.billingAddress),
    city: getPlaceBucuresti(ctx.billingAddress.place),
    judet: efacturaCountrySubentity(ctx.billingAddress, getJudetAbbreviation),
    country: 'RO',
    name: person.company_name
  })
}

/**
 * Factures issues de la borne bois de feu (`xml_macdo`) : les identifiants
 * viennent d'un import et sont irréguliers (CUI parfois sans préfixe, `region`
 * parfois vide). D'où le préfixage RO et — surtout — l'ABSENCE de judeţ par
 * défaut : un `|| "HD"` envoyait des factures en Hunedoara sans que personne
 * ne le voie.
 */
function firewoodPartner (ctx, opts) {
  const identity = resolveBuyerIdentity(ctx, Object.assign({}, opts, { firewood: true }))
  const person = ctx.operation.people[0]
  const judet = efacturaCountrySubentity(ctx.billingAddress, getJudetAbbreviation) ||
    getJudetFromPlace(ctx.billingAddress && ctx.billingAddress.place)
  if (!judet) throw unknownJudetError(ctx.billingAddress && ctx.billingAddress.place)
  return Object.assign(identity, {
    address: efacturaStreetName(ctx.billingAddress),
    city: getPlaceBucuresti((ctx.billingAddress && ctx.billingAddress.place) || ''),
    judet,
    country: 'RO',
    name: person.company_name || ''
  })
}

// ---------------------------------------------------------------------------
// En-tête commun aux deux formes de document
// ---------------------------------------------------------------------------

function newInvoiceDocument () {
  const doc = document.implementation.createDocument('', '', null)
  const invoice = doc.createElementNS('urn:oasis:names:specification:ubl:schema:xsd:Invoice-2', 'Invoice')
  invoice.setAttribute('xmlns:qdt', 'urn:oasis:names:specification:ubl:schema:xsd:QualifiedDataTypes-2')
  invoice.setAttribute('xmlns:ccts', 'urn:un:unece:uncefact:documentation:2')
  invoice.setAttribute('xmlns:udt', 'urn:oasis:names:specification:ubl:schema:xsd:UnqualifiedDataTypes-2')
  invoice.setAttribute('xmlns:cac', 'urn:oasis:names:specification:ubl:schema:xsd:CommonAggregateComponents-2')
  invoice.setAttribute('xmlns:cbc', 'urn:oasis:names:specification:ubl:schema:xsd:CommonBasicComponents-2')
  doc.appendChild(invoice)
  return { doc, invoice }
}

function addDocumentHeader (doc, invoice, operation) {
  const invoiceNumber = operation.invoice_no
  addElement(doc, invoice, 'cbc:UBLVersionID', '2.1')
  addElement(doc, invoice, 'cbc:CustomizationID', 'urn:cen.eu:en16931:2017#compliant#urn:efactura.mfinante.ro:CIUS-RO:1.0.1')
  addElement(doc, invoice, 'cbc:ID', invoiceNumber)
  addElement(doc, invoice, 'cbc:IssueDate', moment(String(operation.date)).format('YYYY-MM-DD'))
  addElement(doc, invoice, 'cbc:DueDate', moment(String(operation.date)).add(15, 'days').format('YYYY-MM-DD'))
  addElement(doc, invoice, 'cbc:InvoiceTypeCode', '380')
  addElement(doc, invoice, 'cbc:TaxPointDate', moment(String(operation.date)).format('YYYY-MM-DD'))
  addElement(doc, invoice, 'cbc:DocumentCurrencyCode', 'RON')
}

function addPaymentMeans (doc, invoice, invoiceNumber) {
  const paymentMeans = addComplexElement(doc, invoice, 'cac:PaymentMeans')
  addElement(doc, paymentMeans, 'cbc:PaymentMeansCode', '1')
  addElement(doc, paymentMeans, 'cbc:PaymentID', invoiceNumber)
  const payeeFinancialAccount = addComplexElement(doc, paymentMeans, 'cac:PayeeFinancialAccount')
  addElement(doc, payeeFinancialAccount, 'cbc:ID', BANK_IBAN)
  const financialInstitutionBranch = addComplexElement(doc, payeeFinancialAccount, 'cac:FinancialInstitutionBranch')
  addElement(doc, financialInstitutionBranch, 'cbc:ID', BANK_NAME)
}

function serialize (doc) {
  const serializer = new XMLSerializer()
  let xmlString = serializer.serializeToString(doc)
  xmlString = `<?xml version="1.0" encoding="UTF-8"?>${  xmlString}`
  return formatXML(xmlString)
}

// ---------------------------------------------------------------------------
// Forme 1 — facture
// ---------------------------------------------------------------------------

function buildInvoice (ctx, resolvePartner) {
  const { operation, lines, totals } = ctx

  // Montants de l'e-factura = totaux du document (somme de ses lignes).
  // Le signe du document est celui de ses lignes. Une facture de régularisation
  // d'avance est NÉGATIVE quand l'avance déduite dépasse la livraison : forcer
  // Math.abs ici donnerait un total positif face à des lignes négatives, soit un
  // écart de 2× le total, et la génération serait bloquée. Un storno complet
  // passe toujours par `buildCreditNote`, qui impose lui-même le signe négatif.
  const total_amount = docHtRon(totals)
  const tva_pct = Math.abs(docVatRateOf(totals))
  const total_tva_ron = docTtcRon(totals)
  const total_tva = parseFloat(total_tva_ron) - parseFloat(total_amount)

  const objMoney = {
    totalht: total_amount,
    totaltva: total_tva,
    totalttc: total_tva_ron,
    pcttva: tva_pct
  }
  const invoiceNumber = operation.invoice_no
  const partner = resolvePartner(ctx, { storno: false })

  const { doc, invoice } = newInvoiceDocument()
  addDocumentHeader(doc, invoice, operation)

  const accountingSupplierParty = addComplexElement(doc, invoice, 'cac:AccountingSupplierParty')
  const supplierParty = addComplexElement(doc, accountingSupplierParty, 'cac:Party')
  addPartyProviderDetails(doc, supplierParty, SUPPLIER.cif, SUPPLIER.address, SUPPLIER.city, SUPPLIER.judet, SUPPLIER.country, SUPPLIER.name, SUPPLIER.vat, SUPPLIER.vat, SUPPLIER.j)

  const accountingCustomerParty = addComplexElement(doc, invoice, 'cac:AccountingCustomerParty')
  const customerParty = addComplexElement(doc, accountingCustomerParty, 'cac:Party')
  addPartyClient(doc, customerParty, partner)

  addPaymentMeans(doc, invoice, invoiceNumber)

  const taxTotal = addComplexElement(doc, invoice, 'cac:TaxTotal')
  addElement(doc, taxTotal, 'cbc:TaxAmount', parseFloat(objMoney.totaltva).toFixed(2)).setAttribute('currencyID', 'RON')
  const taxSubtotal = addComplexElement(doc, taxTotal, 'cac:TaxSubtotal')
  addElement(doc, taxSubtotal, 'cbc:TaxableAmount', parseFloat(objMoney.totalht).toFixed(2)).setAttribute('currencyID', 'RON')
  addElement(doc, taxSubtotal, 'cbc:TaxAmount', parseFloat(objMoney.totaltva).toFixed(2)).setAttribute('currencyID', 'RON')

  const taxCategory = addComplexElement(doc, taxSubtotal, 'cac:TaxCategory')
  if (parseFloat(objMoney.pcttva) === 0) {
    // Si SSD
    if (partner.vat.startsWith('RO')) {
      addElement(doc, taxCategory, 'cbc:ID', 'AE')
      addElement(doc, taxCategory, 'cbc:Percent', parseFloat(objMoney.pcttva).toFixed(2))
      addElement(doc, taxCategory, 'cbc:TaxExemptionReasonCode', 'VATEX-EU-AE')
      addElement(doc, taxCategory, 'cbc:TaxExemptionReason', 'Taxare inversa')
    } else {
      addElement(doc, taxCategory, 'cbc:ID', 'O')
      addElement(doc, taxCategory, 'cbc:Percent', parseFloat(objMoney.pcttva).toFixed(2))
      addElement(doc, taxCategory, 'cbc:TaxExemptionReasonCode', 'VATEX-EU-O')
      addElement(doc, taxCategory, 'cbc:TaxExemptionReason', 'Taxare inversa')
    }
  } else {
    addElement(doc, taxCategory, 'cbc:ID', 'S')
    addElement(doc, taxCategory, 'cbc:Percent', parseFloat(objMoney.pcttva).toFixed(2))
  }
  const taxScheme = addComplexElement(doc, taxCategory, 'cac:TaxScheme')
  addElement(doc, taxScheme, 'cbc:ID', 'VAT')

  const legalMonetaryTotal = addComplexElement(doc, invoice, 'cac:LegalMonetaryTotal')
  addElement(doc, legalMonetaryTotal, 'cbc:LineExtensionAmount', parseFloat(objMoney.totalht).toFixed(2)).setAttribute('currencyID', 'RON')
  addElement(doc, legalMonetaryTotal, 'cbc:TaxExclusiveAmount', parseFloat(objMoney.totalht).toFixed(2)).setAttribute('currencyID', 'RON')
  addElement(doc, legalMonetaryTotal, 'cbc:TaxInclusiveAmount', parseFloat(objMoney.totalttc).toFixed(2)).setAttribute('currencyID', 'RON')
  addElement(doc, legalMonetaryTotal, 'cbc:AllowanceTotalAmount', '0.00').setAttribute('currencyID', 'RON')
  addElement(doc, legalMonetaryTotal, 'cbc:ChargeTotalAmount', '0.00').setAttribute('currencyID', 'RON')
  addElement(doc, legalMonetaryTotal, 'cbc:PrepaidAmount', '0.00').setAttribute('currencyID', 'RON')
  addElement(doc, legalMonetaryTotal, 'cbc:PayableRoundingAmount', '0.00').setAttribute('currencyID', 'RON')
  addElement(doc, legalMonetaryTotal, 'cbc:PayableAmount', parseFloat(objMoney.totalttc).toFixed(2)).setAttribute('currencyID', 'RON')

  let i = 0
  let sumsum = 0
  lines.forEach(pl => {
    let lineextamount = Math.abs(parseFloat(pl.pricetotalron.replace(/\s/g, ''))).toFixed(2)
    if (parseFloat(pl.pricetotalron.replace(/\s/g, '')) < 0) {
      lineextamount = -lineextamount
    }
    sumsum = sumsum + parseFloat(lineextamount)
  })

  // Historiquement, l'écart entre le total et la somme des lignes était injecté
  // dans la première ligne positive pour que l'XML passe la validation ANAF — ce
  // qui MASQUAIT le défaut au lieu de le signaler. Le total vient désormais de la
  // somme des lignes : un écart résiduel est une anomalie, donc bloquant.
  let diff = (parseFloat(parseFloat(objMoney.totalht).toFixed(2)) - parseFloat(sumsum).toFixed(2))
  if (Math.abs(diff) > 0.01) {
    throw totalsMismatchError(
      `e-Factura ${operation.invoice_no || ctx.idoperation} : ` +
      `le total (${parseFloat(objMoney.totalht).toFixed(2)} RON) ne correspond pas ` +
      `à la somme des ${lines.length} ligne(s) (${parseFloat(sumsum).toFixed(2)} RON), ` +
      `écart de ${diff.toFixed(2)}. Génération annulée.`
    )
  }

  lines.forEach(pl => {
    i++
    let quantity = parseFloat(parseFloat(pl.pricetotalron.replace(/\s/g, '')) / parseFloat(pl.unitpriceron.replace(/\s/g, ''))).toFixed(4)
    if (parseFloat(pl.pricetotalron.replace(/\s/g, '')) < 0) quantity = -quantity
    const invoiceLine = addComplexElement(doc, invoice, 'cac:InvoiceLine')
    addElement(doc, invoiceLine, 'cbc:ID', i)
    const invoicedQuantity = addElement(doc, invoiceLine, 'cbc:InvoicedQuantity', parseFloat(quantity).toFixed(4))
    invoicedQuantity.setAttribute('unitCode', 'H87')
    let lineextamount = Math.abs(parseFloat(pl.pricetotalron.replace(/\s/g, ''))).toFixed(2)
    if (parseFloat(pl.pricetotalron.replace(/\s/g, '')) < 0) lineextamount = -lineextamount
    // Résidu d'arrondi (|diff| <= 0.01) : absorbé par la première ligne positive.
    if (parseFloat(lineextamount) >= 0 && diff >= 0.009) {
      const max2decimal = parseFloat(lineextamount) + Math.abs(diff)
      const element = addElement(doc, invoiceLine, 'cbc:LineExtensionAmount', parseFloat(max2decimal).toFixed(2))
      element.setAttribute('currencyID', 'RON')
      diff = 0
    } else if (parseFloat(lineextamount) >= 0 && diff <= -0.009) {
      const max2decimal = parseFloat(lineextamount) - Math.abs(diff)
      const element = addElement(doc, invoiceLine, 'cbc:LineExtensionAmount', parseFloat(max2decimal).toFixed(2))
      element.setAttribute('currencyID', 'RON')
      diff = 0
    } else {
      addElement(doc, invoiceLine, 'cbc:LineExtensionAmount', lineextamount).setAttribute('currencyID', 'RON')
    }
    const item = addComplexElement(doc, invoiceLine, 'cac:Item')
    addElement(doc, item, 'cbc:Name', pl.description)
    const classifiedTaxCategory = addComplexElement(doc, item, 'cac:ClassifiedTaxCategory')
    // Pour être 0% il faut être SSD ou SFDD
    if (parseFloat(objMoney.pcttva) === 0) {
      if (partner.vat.startsWith('RO')) {
        addElement(doc, classifiedTaxCategory, 'cbc:ID', 'AE')
        addElement(doc, classifiedTaxCategory, 'cbc:Percent', parseFloat(objMoney.pcttva).toFixed(2))
      } else {
        addElement(doc, classifiedTaxCategory, 'cbc:ID', 'O')
        addElement(doc, classifiedTaxCategory, 'cbc:Percent', parseFloat(objMoney.pcttva).toFixed(2))
      }
    } else {
      addElement(doc, classifiedTaxCategory, 'cbc:ID', 'S')
      addElement(doc, classifiedTaxCategory, 'cbc:Percent', parseFloat(objMoney.pcttva).toFixed(2))
    }
    const itemTaxScheme = addComplexElement(doc, classifiedTaxCategory, 'cac:TaxScheme')
    addElement(doc, itemTaxScheme, 'cbc:ID', 'VAT')
    const price = addComplexElement(doc, invoiceLine, 'cac:Price')
    addElement(doc, price, 'cbc:PriceAmount', Math.abs(parseFloat(pl.unitpriceron.replace(/\s/g, ''))).toFixed(2)).setAttribute('currencyID', 'RON')
    const baseQuantity = addElement(doc, price, 'cbc:BaseQuantity', parseFloat(quantity).toFixed(4))
    baseQuantity.setAttribute('unitCode', 'H87')
  })

  return serialize(doc)
}

// ---------------------------------------------------------------------------
// Forme 2 — storno (note de crédit)
// ---------------------------------------------------------------------------

function toNumber (value) {
  const n = parseFloat(value)
  return Number.isFinite(n) ? n : 0
}

/** Arrondi comptable à 2 décimales (miroir de api/app/helpers/documentAmounts.js). */
function round2 (value) {
  return Math.round((toNumber(value) + Number.EPSILON) * 100) / 100
}

/** Montant d'une ligne tel que l'API le formate (« 1 234.56 »), en valeur absolue. */
function absAmount (formatted) {
  const text = formatted === null || formatted === undefined ? '' : String(formatted)
  return Math.abs(toNumber(text.replace(/\s/g, '')))
}

/** Valeur NÉGATIVE à `decimals` décimales — et « 0.00 », jamais « -0.00 ». */
function negative (value, decimals) {
  const n = Math.abs(toNumber(value))
  return (n === 0 ? 0 : -n).toFixed(decimals)
}

/**
 * Montants de référence d'un storno : ceux de la facture qu'il annule.
 *
 * Un avoir généré par l'ERP n'a qu'une ligne (« Services Cancellation
 * (Invoice: …) ») au prix du total HT annulé, et sa TVA est recalculée sur
 * cette seule ligne ; la facture, elle, a sa TVA calculée ligne par ligne puis
 * additionnée. Les deux arrondis divergent d'un centime : RO2026_01680
 * (annule RO2026_01525) partait avec −17,35 de TVA et −99,99 de total pour une
 * facture à 17,36 / 100,00 — chez l'ANAF, les deux documents ne s'annulaient
 * pas. L'API joint donc aux totaux d'un storno ceux de la facture annulée
 * (`totals.storno_of`, calculés par la fonction même qui a produit l'e-factura
 * de la facture), et ce sont eux que le storno reprend, en négatif.
 *
 * Renvoie null — le storno garde alors ses propres montants — quand il
 * n'annule qu'une PARTIE de la facture (HT différent), ou quand la facture
 * porte plusieurs taux de TVA : le storno n'a qu'une ligne, à un seul taux, et
 * ne peut pas reproduire cette ventilation.
 */
function cancelledInvoiceAmounts (totals, stornoHt, stornoRate) {
  const ref = totals && totals.storno_of
  if (!ref) return null
  const ht = Math.abs(toNumber(ref.ht_ron))
  const ttc = Math.abs(toNumber(ref.ttc_ron))
  if (!ht || round2(Math.abs(ht - stornoHt)) > 0.01) return null
  const rates = (ref.by_vat || []).filter((b) => toNumber(b.base_ron) !== 0)
  if (rates.length !== 1 || Math.abs(toNumber(rates[0].tva_pct) - stornoRate) > 0.001) return null
  return { ht, ttc, tva: round2(ttc - ht) }
}

function buildCreditNote (ctx, resolvePartner) {
  const { operation, lines, totals } = ctx

  // Un storno porte les MÊMES montants que la facture d'origine, en négatif :
  // le signe est imposé ici, pas déduit des lignes. Les montants eux-mêmes
  // sont ceux de la facture annulée quand elle est connue (voir
  // cancelledInvoiceAmounts), sinon ceux des lignes du storno.
  const ownHt = Math.abs(docHtRon(totals))
  const ownTtc = Math.abs(docTtcRon(totals))
  const tva_pct = Math.abs(docVatRateOf(totals))
  const amounts = cancelledInvoiceAmounts(totals, ownHt, tva_pct) ||
    { ht: ownHt, ttc: ownTtc, tva: round2(ownTtc - ownHt) }

  const objMoney = {
    totalht: amounts.ht,
    totaltva: amounts.tva,
    totalttc: amounts.ttc,
    pcttva: tva_pct
  }
  const invoiceNumber = operation.invoice_no
  // Société identifiée obligatoire (resolveBuyerIdentity, storno: true) : un
  // storno n'a pas de branche « personne physique ».
  const partner = resolvePartner(ctx, { storno: true })

  // Les lignes du storno, en valeur absolue : leur somme DOIT être le total HT.
  // Quand le total vient de la facture annulée, l'écart admis est d'un centime
  // (cf. cancelledInvoiceAmounts) ; il est absorbé par la première ligne, comme
  // le résidu d'arrondi d'une facture.
  const lineAmounts = lines.map((pl) => absAmount(pl.pricetotalron))
  const sumLines = round2(lineAmounts.reduce((acc, n) => acc + n, 0))
  let residual = round2(objMoney.totalht - sumLines)
  if (Math.abs(residual) > 0.01) {
    throw totalsMismatchError(
      `Storno ${operation.invoice_no || ctx.idoperation} : ` +
      `le total (${objMoney.totalht.toFixed(2)} RON) ne correspond pas ` +
      `à la somme des ${lines.length} ligne(s) (${sumLines.toFixed(2)} RON), ` +
      `écart de ${residual.toFixed(2)}. Génération annulée.`
    )
  }

  const { doc, invoice } = newInvoiceDocument()
  addDocumentHeader(doc, invoice, operation)

  // Même bloc émetteur que la facture : PartyIdentification/cbc:ID = RO33531190.
  // Le storno partait avec le CIF nu (33531190), différent de celui de la
  // facture qu'il annule.
  const accountingSupplierParty = addComplexElement(doc, invoice, 'cac:AccountingSupplierParty')
  const supplierParty = addComplexElement(doc, accountingSupplierParty, 'cac:Party')
  addPartyProviderDetails(doc, supplierParty, SUPPLIER.cif, SUPPLIER.address, SUPPLIER.city, SUPPLIER.judet, SUPPLIER.country, SUPPLIER.name, SUPPLIER.vat, SUPPLIER.vat, SUPPLIER.j)

  const accountingCustomerParty = addComplexElement(doc, invoice, 'cac:AccountingCustomerParty')
  const customerParty = addComplexElement(doc, accountingCustomerParty, 'cac:Party')
  addPartyClient(doc, customerParty, partner)

  addPaymentMeans(doc, invoice, invoiceNumber)

  const taxTotal = addComplexElement(doc, invoice, 'cac:TaxTotal')
  addElement(doc, taxTotal, 'cbc:TaxAmount', negative(objMoney.totaltva, 2)).setAttribute('currencyID', 'RON')
  const taxSubtotal = addComplexElement(doc, taxTotal, 'cac:TaxSubtotal')
  addElement(doc, taxSubtotal, 'cbc:TaxableAmount', negative(objMoney.totalht, 2)).setAttribute('currencyID', 'RON')
  addElement(doc, taxSubtotal, 'cbc:TaxAmount', negative(objMoney.totaltva, 2)).setAttribute('currencyID', 'RON')

  const taxCategory = addComplexElement(doc, taxSubtotal, 'cac:TaxCategory')
  if (parseFloat(objMoney.pcttva) === 0) {
    // Si SSD
    addElement(doc, taxCategory, 'cbc:ID', 'AE')
    addElement(doc, taxCategory, 'cbc:Percent', parseFloat(objMoney.pcttva).toFixed(2))
    addElement(doc, taxCategory, 'cbc:TaxExemptionReasonCode', 'VATEX-EU-AE')
    addElement(doc, taxCategory, 'cbc:TaxExemptionReason', 'Taxare inversa')
  } else {
    addElement(doc, taxCategory, 'cbc:ID', 'S')
    addElement(doc, taxCategory, 'cbc:Percent', parseFloat(objMoney.pcttva).toFixed(2))
  }
  const taxScheme = addComplexElement(doc, taxCategory, 'cac:TaxScheme')
  addElement(doc, taxScheme, 'cbc:ID', 'VAT')

  const legalMonetaryTotal = addComplexElement(doc, invoice, 'cac:LegalMonetaryTotal')
  addElement(doc, legalMonetaryTotal, 'cbc:LineExtensionAmount', negative(objMoney.totalht, 2)).setAttribute('currencyID', 'RON')
  addElement(doc, legalMonetaryTotal, 'cbc:TaxExclusiveAmount', negative(objMoney.totalht, 2)).setAttribute('currencyID', 'RON')
  addElement(doc, legalMonetaryTotal, 'cbc:TaxInclusiveAmount', negative(objMoney.totalttc, 2)).setAttribute('currencyID', 'RON')
  addElement(doc, legalMonetaryTotal, 'cbc:AllowanceTotalAmount', '0.00').setAttribute('currencyID', 'RON')
  addElement(doc, legalMonetaryTotal, 'cbc:ChargeTotalAmount', '0.00').setAttribute('currencyID', 'RON')
  addElement(doc, legalMonetaryTotal, 'cbc:PrepaidAmount', '0.00').setAttribute('currencyID', 'RON')
  addElement(doc, legalMonetaryTotal, 'cbc:PayableRoundingAmount', '0.00').setAttribute('currencyID', 'RON')
  addElement(doc, legalMonetaryTotal, 'cbc:PayableAmount', negative(objMoney.totalttc, 2)).setAttribute('currencyID', 'RON')

  lines.forEach((pl, index) => {
    const unitPrice = absAmount(pl.unitpriceron)
    // Quantité déduite du montant : `quantity` est une chaîne formatée avec l'unité.
    const quantity = unitPrice ? lineAmounts[index] / unitPrice : 0
    let amount = lineAmounts[index]
    if (residual !== 0) {
      amount = round2(amount + residual)
      residual = 0
    }
    const invoiceLine = addComplexElement(doc, invoice, 'cac:InvoiceLine')
    addElement(doc, invoiceLine, 'cbc:ID', index + 1)

    // Storno : la quantité FACTURÉE est négative (c'est elle qui annule) et la
    // quantité de BASE du prix reste positive (un prix s'exprime par pièce, pas
    // par −1 pièce). L'inverse — +1 facturé, prix pour −1 — faisait lire à
    // l'ANAF une livraison de 1 à prix négatif.
    const invoicedQuantity = addElement(doc, invoiceLine, 'cbc:InvoicedQuantity', negative(quantity, 4))
    invoicedQuantity.setAttribute('unitCode', 'H87')
    addElement(doc, invoiceLine, 'cbc:LineExtensionAmount', negative(amount, 2)).setAttribute('currencyID', 'RON')
    const item = addComplexElement(doc, invoiceLine, 'cac:Item')
    addElement(doc, item, 'cbc:Name', pl.description)
    const classifiedTaxCategory = addComplexElement(doc, item, 'cac:ClassifiedTaxCategory')
    // Pour être 0% il faut être SSD ou SFDD
    if (parseFloat(objMoney.pcttva) === 0) {
      addElement(doc, classifiedTaxCategory, 'cbc:ID', 'AE')
      addElement(doc, classifiedTaxCategory, 'cbc:Percent', parseFloat(objMoney.pcttva).toFixed(2))
    } else {
      addElement(doc, classifiedTaxCategory, 'cbc:ID', 'S')
      addElement(doc, classifiedTaxCategory, 'cbc:Percent', parseFloat(objMoney.pcttva).toFixed(2))
    }
    const itemTaxScheme = addComplexElement(doc, classifiedTaxCategory, 'cac:TaxScheme')
    addElement(doc, itemTaxScheme, 'cbc:ID', 'VAT')
    const price = addComplexElement(doc, invoiceLine, 'cac:Price')
    addElement(doc, price, 'cbc:PriceAmount', unitPrice.toFixed(2)).setAttribute('currencyID', 'RON')
    const baseQuantity = addElement(doc, price, 'cbc:BaseQuantity', Math.abs(quantity).toFixed(4))
    baseQuantity.setAttribute('unitCode', 'H87')
  })

  return serialize(doc)
}

// ---------------------------------------------------------------------------
// API publique
// ---------------------------------------------------------------------------

/** Facture ERP. */
export function buildEfacturaUbl (ctx) {
  return buildInvoice(ctx, standardPartner)
}

/** Note de crédit ERP. */
export function buildEfacturaUblStorno (ctx) {
  return buildCreditNote(ctx, standardPartner)
}

/** Facture issue de la borne bois de feu. */
export function buildEfacturaUblFirewood (ctx) {
  return buildInvoice(ctx, firewoodPartner)
}

/** Note de crédit issue de la borne bois de feu. */
export function buildEfacturaUblFirewoodStorno (ctx) {
  return buildCreditNote(ctx, firewoodPartner)
}

/**
 * Choisit la variante d'après le document lui-même — c'est ce que fait le
 * routeur quand il redirige `/invoice/:id` vers `/firewood-invoice/:id` sur
 * `xml_macdo`. Une facture borne envoyée par la liste porte donc exactement le
 * XML de sa fiche.
 */
export function buildEfacturaXml (ctx) {
  const op = ctx.operation || {}
  const firewood = !!op.xml_macdo
  if (op.iscreditnote) return firewood ? buildEfacturaUblFirewoodStorno(ctx) : buildEfacturaUblStorno(ctx)
  return firewood ? buildEfacturaUblFirewood(ctx) : buildEfacturaUbl(ctx)
}

export function downloadXml (xmlString, fileName) {
  const blob = new Blob([xmlString], { type: 'application/xml' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

// ---------------------------------------------------------------------------
// Adresse acheteur : ce que l'ANAF refuserait, AVANT l'envoi
// ---------------------------------------------------------------------------

// Miroir de api/app/helpers/efacturaAddress.js : les SEULES règles CIUS-RO de
// l'adresse acheteur (schématron ANAF RO16931 1.0.9), rien de plus strict.
// Une adresse « localité seule » passe : StreetName = « Localité CP ».
const RO_COUNTY_CODES = [
  'RO-AB', 'RO-AG', 'RO-AR', 'RO-B', 'RO-BC', 'RO-BH', 'RO-BN', 'RO-BR', 'RO-BT', 'RO-BV', 'RO-BZ',
  'RO-CJ', 'RO-CL', 'RO-CS', 'RO-CT', 'RO-CV', 'RO-DB', 'RO-DJ', 'RO-GJ', 'RO-GL', 'RO-GR', 'RO-HD',
  'RO-HR', 'RO-IF', 'RO-IL', 'RO-IS', 'RO-MH', 'RO-MM', 'RO-MS', 'RO-NT', 'RO-OT', 'RO-PH', 'RO-SB',
  'RO-SJ', 'RO-SM', 'RO-SV', 'RO-TL', 'RO-TM', 'RO-TR', 'RO-VL', 'RO-VN', 'RO-VS'
]
const BUCHAREST_SECTORS = ['SECTOR1', 'SECTOR2', 'SECTOR3', 'SECTOR4', 'SECTOR5', 'SECTOR6']

const normSpace = (v) => String(v === null || v === undefined ? '' : v).replace(/\s+/g, ' ').trim()

function buyerAddressProblems (a) {
  const street = normSpace(a.street)
  const city = normSpace(a.city)
  const county = normSpace(a.county)
  const country = normSpace(a.country).toUpperCase()
  const problems = []
  if (!street) problems.push('The address line (street) is empty.')
  else if (street.length > 150) problems.push(`The address line is too long (${street.length} characters, max 150).`)
  if (!city) problems.push('The locality (city) is empty.')
  else if (city.length > 50) problems.push(`The locality is too long (${city.length} characters, max 50).`)
  if (country === 'RO') {
    if (!county) problems.push('The county (județ) is missing.')
    else if (RO_COUNTY_CODES.indexOf(county) < 0) problems.push(`"${county}" is not a valid county code.`)
    else if (county === 'RO-B' && city && BUCHAREST_SECTORS.indexOf(city) < 0) {
      problems.push(`In Bucharest the locality must be a sector (SECTOR1 to SECTOR6), found "${city}".`)
    }
  }
  return problems
}

/**
 * Ce que l'ANAF reprocherait à l'adresse de facturation, calculée comme les
 * partenaires ci-dessus la mettront dans l'XML. [] = rien à redire.
 * `firewood` : facture borne, dont le judeţ a un repli sur le nom de la localité.
 */
export function efacturaBuyerAddressProblems (billingAddress, { firewood = false } = {}) {
  const address = billingAddress || {}
  const place = String(address.place || '')
  const county = efacturaCountrySubentity(address, getJudetAbbreviation) ||
    (firewood ? getJudetFromPlace(place) : '')
  return buyerAddressProblems({
    street: efacturaStreetName(address),
    city: getPlaceBucuresti(place),
    county,
    country: 'RO'
  })
}
